import type { AuthAccessContext } from "@/types/authAccess";
import {
  ACCESS_MODULE_KEYS,
  ACCESS_MODULE_ROUTE_RULES,
  ACCESS_PERMISSION_ROUTE_RULES,
} from "@/constants/accessControl";

const ALWAYS_ALLOWED_ROUTE_PREFIXES = [
  "/profile",
  "/client-profile",
  "/client/dashboard-v2",
  "/subscription",
  "/security",
  "/account-settings",
];

const ROUTE_ACCESS_ALIASES: Array<{
  test: (path: string) => boolean;
  candidates: string[];
}> = [
    {
      test: (path) => path.startsWith("/clients/") || path.startsWith("/users/clients/"),
      candidates: ["/clients"],
    },
    {
      test: (path) => path === "/dmp-accounts" || path.startsWith("/dmp-accounts/"),
      candidates: ["/dmp-accounts", "/clients"],
    },
    {
      test: (path) => path === "/renewal-tracking" || path.startsWith("/renewal-tracking/"),
      candidates: ["/clients"],
    },
    {
      test: (path) => path === "/reports/dashboard-preview" || path.startsWith("/reports/"),
      candidates: ["/reports"],
    },
    {
      test: (path) => path.startsWith("/leads/"),
      candidates: ["/leads"],
    },
    {
      test: (path) => path === "/social-media" || path.startsWith("/social-media/"),
      candidates: ["/social-media"],
    },
    {
      test: (path) => path === "/apps/social-media" || path.startsWith("/apps/social-media/"),
      candidates: ["/apps/social-media", "/social-media"],
    },
    {
      test: (path) => path.startsWith("/apps/tasks/"),
      candidates: ["/apps/task-dashboard", "/operations/task"],
    },
    {
      test: (path) => path === "/operations/task",
      candidates: ["/apps/task-dashboard", "/operations/task"],
    },
    {
      test: (path) => path.startsWith("/admin/package-services"),
      candidates: ["/admin/package-services"],
    },
    {
      test: (path) => path.startsWith("/renewals"),
      candidates: ["/renewals"],
    },
    {
      test: (path) =>
        path === "/onboarding/microapp" ||
        path.startsWith("/onboarding/microapp/"),
      candidates: ["/onboarding/microapp"],
    },
    {
      test: (path) =>
        path === "/onboarding" ||
        (path.startsWith("/onboarding/") && !path.startsWith("/onboarding/microapp/")) ||
        path === "/client/onboarding" ||
        path.startsWith("/client/onboarding/") ||
        path === "/apps/onboarding" ||
        path.startsWith("/apps/onboarding/"),
      candidates: ["/onboarding", "/client/onboarding", "/apps/onboarding"],
    },
  ];

function normalizeAccessKey(value: unknown): string {
  return String(value || "").trim();
}

function createLookup(values: unknown[] | undefined | null): Set<string> {
  return new Set((values || []).map(normalizeAccessKey).filter(Boolean));
}

export function shouldUseClientDefaultFallback(
  accessContext: AuthAccessContext | null | undefined
): boolean {
  if (!accessContext) {
    return false;
  }

  const hasDefaultOrMissingRole =
    !accessContext.role || accessContext.role.role_key === "client_default";

  return (
    hasDefaultOrMissingRole &&
    !accessContext.module_access.has_explicit_assignments &&
    accessContext.sidebar.sections.length === 0
  );
}

function matchesPrefix(path: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function isAlwaysAllowedRoute(path: string): boolean {
  return matchesPrefix(path, ALWAYS_ALLOWED_ROUTE_PREFIXES);
}

export function buildRouteAccessCandidates(path: string): string[] {
  const normalized = String(path || "").trim() || "/";
  const candidates = new Set<string>([normalized]);

  for (const alias of ROUTE_ACCESS_ALIASES) {
    if (alias.test(normalized)) {
      alias.candidates.forEach((candidate) => candidates.add(candidate));
    }
  }

  return [...candidates];
}

export function canAccessPathFromContext(
  path: string,
  accessContext: AuthAccessContext | null | undefined
): boolean | null {
  if (!accessContext) {
    return null;
  }

  if (accessContext.role?.is_superadmin) {
    return true;
  }

  if (isAlwaysAllowedRoute(path)) {
    return true;
  }

  const allowedRoutes = createLookup(accessContext.sidebar.allowed_route_paths);

  const matchedPermissionRoute = ACCESS_PERMISSION_ROUTE_RULES.find(({ prefixes }) =>
    matchesPrefix(path, prefixes)
  );

  const visibleModuleKeys = createLookup(
    accessContext.module_access?.effective_visible_module_keys
  );
  const allowedPermissionKeys = createLookup(
    accessContext.permission_access?.effective_allowed_permission_keys
  );

  if (allowedRoutes.size === 0) {
    if (matchedPermissionRoute) {
      return (
        Boolean(accessContext.role?.is_superadmin) ||
        allowedPermissionKeys.has(matchedPermissionRoute.permissionKey)
      );
    }

    const matchedModule = ACCESS_MODULE_ROUTE_RULES.find(({ prefixes }) =>
      matchesPrefix(path, prefixes)
    );

    return matchedModule
      ? visibleModuleKeys.has(matchedModule.moduleKey)
      : false;
  }

  if (matchedPermissionRoute) {
    return (
      Boolean(accessContext.role?.is_superadmin) ||
      allowedPermissionKeys.has(matchedPermissionRoute.permissionKey)
    );
  }

  const routeAllowed = buildRouteAccessCandidates(path).some((candidate) =>
    allowedRoutes.has(candidate)
  );

  const matchedModule = ACCESS_MODULE_ROUTE_RULES.find(({ prefixes }) =>
    matchesPrefix(path, prefixes)
  );

  if (matchedModule) {
    return routeAllowed && visibleModuleKeys.has(matchedModule.moduleKey);
  }

  return routeAllowed;
}

export function getBestDefaultRouteFromContext(
  accessContext: AuthAccessContext | null | undefined
): string {
  if (accessContext?.role?.is_superadmin) {
    return "/overview";
  }

  const allowedRoutes = (accessContext?.sidebar.allowed_route_paths || []).filter(Boolean);
  const visibleModuleKeys = createLookup(
    accessContext?.module_access?.effective_visible_module_keys
  );

  if (allowedRoutes.includes("/overview")) {
    return "/overview";
  }

  if (allowedRoutes.includes("/apps/task-dashboard")) {
    return "/apps/task-dashboard";
  }

  if (allowedRoutes.includes("/operations/task")) {
    return "/operations/task";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.OVERVIEW)) {
    return "/overview";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.TASKS)) {
    return "/apps/task-dashboard";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.ONBOARDING)) {
    return "/onboarding";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.ONBOARDING_MICROAPP)) {
    return "/onboarding/microapp";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.CLIENTS)) {
    return "/clients";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.USERS)) {
    return "/users";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.CHAT)) {
    return "/communication/chat";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.REPORTS)) {
    return "/reports";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.SOCIAL_MEDIA)) {
    return "/social-media";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.LEADS)) {
    return "/leads";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.PACKAGE_SERVICES)) {
    return "/admin/package-services";
  }

  if (visibleModuleKeys.has(ACCESS_MODULE_KEYS.AUDIT_TRAIL)) {
    return "/activity";
  }

  return allowedRoutes[0] || "";
}
